import i18n from '../../i18n/i18n';
import { tr } from '../../i18n/i18n';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { IErrorResponse } from '../../interface/main.interface';
import {
  ICreateStudentError,
  IGetGuardiansReposne,
  IGetStudentByIdResponse,
  IBulkActivationResponse,
  ISendStudentInvitationPayload,
  ISendStudentInvitationResponse,
} from '../../interface/studentModel';
import { getById, studentService } from '../../services/student';

export const fetchStudentById = createAsyncThunk<
  IGetStudentByIdResponse,
  string,
  { rejectValue: ICreateStudentError }
>('student/get-one', async (id, { rejectWithValue }) => {
  try {
    const response = await getById(id);
    return response as unknown as IGetStudentByIdResponse;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateStudentError>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_error_occurred', 'An error occurred') });
    } else {
      // Handle non-Axios errors (like Error objects thrown from getById)
      return rejectWithValue({ message: err?.message || i18n.t('toast.an_unexpected_error_occurred', 'An unexpected error occurred') });
    }
  }
});

/**
 * Async thunk to fetch a paginated list of guardians for a specific student.
 */
export const getGuardiansForStudent = createAsyncThunk<
  IGetGuardiansReposne,
  string,
  { rejectValue: IErrorResponse }
>('guardian/getForStudent', async (studentId, { rejectWithValue }) => {
  try {
    const response = await studentService.getGuardiansForStudent(studentId);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred') });
    }
    return rejectWithValue({ message: err.message });
  }
});

/**
 * Async thunk to bulk activate multiple students
 */
export const bulkActivateStudents = createAsyncThunk<
  IBulkActivationResponse, // Success response type: extracted nested data
  string[], // Payload: array of student IDs
  { rejectValue: ICreateStudentError }
>('student/bulkActivate', async (studentIds, { rejectWithValue }) => {
  try {
    // Call service method which returns full response wrapper
    const response = await studentService.bulkActivateStudents(studentIds);
    // Extract the nested data property and return it
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateStudentError>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.activation_failed', i18n.t('toast.activation_failed', 'Activation failed')) });
    }
    // Handle non-Axios errors
    return rejectWithValue({ message: err?.message || i18n.t('toast.an_unexpected_error_occurred', 'An unexpected error occurred') });
  }
});

/**
 * Async thunk to send (or resend) login-credential invitations to the selected
 * students. The backend endpoint is idempotent for resends — invoking it for an
 * already-invited student simply re-sends the invitation.
 */
export const studentSendInvitation = createAsyncThunk<
  ISendStudentInvitationResponse,
  ISendStudentInvitationPayload,
  { rejectValue: ICreateStudentError }
>('student/send-invitation', async (payload, { rejectWithValue }) => {
  try {
    const response = await studentService.sendInvitation(payload);
    return response.data;
  } catch (err: any) {
    // The axios response interceptor rejects with the backend response body
    // itself (not an AxiosError), so read fields off the body directly.
    return rejectWithValue(
      err?.message ? { message: err.message } : { message: i18n.t('toast.failed_to_send_invitations', 'Failed to send invitations') },
    );
  }
});

export const studentActions = {
  fetchStudentById,
  getGuardiansForStudent,
  bulkActivateStudents,
  studentSendInvitation,
};
